<template>
	<div class="new-back fill-height py-5 px-3">
		<div class="d-flex flex-wrap align-center mb-3 effort-report-filters" style="gap: 10px">
			<q-date-picker
				v-model="filters.startDate"
				@input="onFilterChange"
				label="Start Date"
				outlined hide-details inner dense
				class="effort-filter"
			/>
			<q-date-picker
				v-model="filters.endDate"
				@input="onFilterChange"
				label="End Date"
				outlined hide-details inner dense
				class="effort-filter"
			/>
			<v-autocomplete
				v-model="filters.projectIds"
				@change="onFilterChange"
				:items="projects"
				label="Project"
				item-value="id" item-text="name"
				:filter="(item, queryText) => normalizeIncludes(item.name + ' ' + (item.smallCode || ''), queryText)"
				:menu-props="filterMenuProps"
				outlined dense hide-details clearable multiple
				class="effort-filter"
			/>
			<v-autocomplete
				v-model="filters.billableIds"
				@change="onFilterChange"
				:items="billables"
				label="Billable"
				item-value="id" item-text="code"
				:menu-props="filterMenuProps"
				outlined dense hide-details clearable multiple
				class="effort-filter"
			/>
			<v-autocomplete
				v-model="filters.billableTypes"
				@change="onFilterChange"
				:items="billableTypes"
				label="Billable Type"
				item-value="id" item-text="code"
				:menu-props="filterMenuProps"
				outlined dense hide-details clearable multiple
				class="effort-filter"
			/>
			<q-user-autocomplete
				v-model="filters.reporterIds"
				@input="onFilterChange"
				:items="users"
				label="Reporter"
				:menu-props="filterMenuProps"
				outlined dense hide-details clearable multiple
				class="effort-filter"
			/>
			<v-select
				v-model="filters.hasBillable"
				@change="onFilterChange"
				:items="hasBillableOptions"
				label="Billable Link"
				item-value="value" item-text="text"
				:menu-props="filterMenuProps"
				outlined dense hide-details
				class="effort-filter"
			/>
		</div>

		<v-data-table
			class="rounded-lg bordered new-back lighten2"
			:headers="headers"
			:items="items"
			:server-items-length="pagination.totalItems"
			:items-per-page.sync="pagination.pageSize"
			:page.sync="pagination.page"
			@pagination="fetchReport"
			:loading="loading"
			disable-sort
		>
			<template #[`item.reporterBlueTag`]="{ item }">
				<v-tooltip right content-class="effort-report-bluetag-tooltip" :nudge-right="8">
					<template #activator="{ on }">
						<span v-on="on">{{ item.reporterBlueTag || '-' }}</span>
					</template>
					<span>{{ item.reporterFullName || item.reporterBlueTag || '-' }}</span>
				</v-tooltip>
			</template>
			<template #[`item.date`]="{ item }">
				{{ item.date | formatDateCustom('(ddd) MMM D, YYYY') }}
			</template>
			<template #[`item.description`]="{ item }">
				{{ stripHtml(item.description) }}
			</template>
			<template #[`item.executed`]="{ item }">
				{{ item.executed | number(2) }}
			</template>
			<template #[`item.isBillable`]="{ item }">
				<v-btn
					:color="isBillableStyle(item.isBillable).color"
					icon small
					:style="($vuetify.theme.isDark ? 'border: 2px solid ' : 'background: ') + isBillableStyle(item.isBillable).lightColor"
					readonly
					tabindex="-1"
				>
					<v-icon>mdi-currency-usd</v-icon>
				</v-btn>
			</template>
			<template #[`item.billableLineRole`]="{ item }">
				{{ item.billableLineRole || '-' }}
			</template>
			<template #[`item.billableCode`]="{ item }">
				{{ item.billableCode || 'NA' }}
			</template>
		</v-data-table>
	</div>
</template>

<script>
import moment from 'moment'

export default {
	data: () => ({
		loading: false,
		fetchSeq: 0,
		filters: {
			startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
			endDate: moment().format('YYYY-MM-DD'),
			projectIds: [],
			billableIds: [],
			billableTypes: [],
			reporterIds: [],
			hasBillable: null,
		},
		hasBillableOptions: [
			{ text: 'All', value: null },
			{ text: 'With Billable', value: true },
			{ text: 'Without Billable', value: false },
		],
		filterMenuProps: {
			offsetY: true,
			bottom: true,
			nudgeBottom: 4,
			allowOverflow: true,
			contentClass: 'effort-report-filter-menu',
		},
		pagination: {
			page: 1,
			pageSize: 10,
			totalItems: 0,
		},
		items: [],
		users: [],
		projects: [],
		billables: [],
		billableTypes: [],
		billableColors: {
			true: { color: 'success', lightColor: '#bce3c9', text: 'Billable' },
			false: { color: 'error', lightColor: '#f5c4c5', text: 'Non-Billable' },
			pending: { color: 'gray', lightColor: '#e4e4e4', text: 'Pending' },
		},
		headers: [
			{ text: 'Bluetag', value: 'reporterBlueTag' },
			{ text: 'Date', value: 'date' },
			{ text: 'Project', value: 'projectName' },
			{ text: 'Description', value: 'description' },
			{ text: 'Executed', value: 'executed', align: 'center' },
			{ text: 'Category', value: 'categoryName' },
			{ text: 'Subcategory', value: 'subcategoryName' },
			{ text: 'Is Billable', value: 'isBillable', align: 'center', sortable: false },
			{ text: 'Service', value: 'billableLineRole' },
			{ text: 'Billable', value: 'billableCode' },
		],
	}),
	created() {
		document.title = 'Effort Report'
		this.$q.log(53, 16, 3, 'NA')
		this.setUserPreferences('EffortReport', 'filters')
		this.normalizeDates()
		this.init()
	},
	methods: {
		init() {
			this.$http.get('api/user?onlyInterns=true&usersOnly=true')
				.then(res => {
					this.users = (res.data || []).filter(x => x.blueTag)
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Failed to load users')
				})

			this.$http.get('api/project?all=true')
				.then(res => {
					this.projects = res.data || []
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Failed to load projects')
				})

			this.$http.get('api/billable')
				.then(res => {
					this.billables = res.data || []
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Failed to load billables')
				})

			this.$http.get('api/enums/admin/billable/type')
				.then(res => {
					this.billableTypes = res.data || []
				})
				.catch(err => {
					console.error(err)
					this.$root.error('Failed to load billable types')
				})

			this.fetchReport()
		},
		normalizeDates() {
			if (this.filters.startDate)
				this.filters.startDate = moment(this.filters.startDate).format('YYYY-MM-DD')
			else
				this.filters.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')

			if (this.filters.endDate)
				this.filters.endDate = moment(this.filters.endDate).format('YYYY-MM-DD')
			else
				this.filters.endDate = moment().format('YYYY-MM-DD')
		},
		onFilterChange() {
			this.$nextTick(() => {
				this.normalizeDates()
				this.pagination.page = 1
				this.fetchReport()
			})
		},
		fetchReport() {
			this.normalizeDates()
			const seq = ++this.fetchSeq
			this.loading = true
			this.$http.post('api/report/effortReport', {
				startDate: this.filters.startDate,
				endDate: this.filters.endDate,
				projectIds: this.filters.projectIds || [],
				billableIds: this.filters.billableIds || [],
				billableTypes: this.filters.billableTypes || [],
				reporterIds: this.filters.reporterIds || [],
				hasBillable: this.filters.hasBillable,
				page: this.pagination.page,
				pageSize: this.pagination.pageSize === 'all' ? -1 : this.pagination.pageSize,
			})
				.then(res => {
					if (seq !== this.fetchSeq) return
					this.items = res.data.items || []
					this.pagination.totalItems = res.data.totalItems || 0
				})
				.catch(err => {
					if (seq !== this.fetchSeq) return
					console.error(err)
					this.$root.error('Failed to load effort report')
				})
				.finally(() => {
					if (seq === this.fetchSeq)
						this.loading = false
				})
		},
		stripHtml(html) {
			if (!html) return ''
			const tmp = document.createElement('div')
			tmp.innerHTML = html
			return tmp.textContent || tmp.innerText || ''
		},
		isBillableStyle(value) {
			if (value === true) return this.billableColors.true
			if (value === false) return this.billableColors.false
			return this.billableColors.pending
		},
	},
}
</script>

<style lang="scss" scoped>
.effort-report-filters {
	.effort-filter {
		width: 200px;
		flex: 0 0 200px;
		max-width: 200px;
	}

	::v-deep .effort-filter {
		width: 200px;
		flex: 0 0 200px;
		max-width: 200px;

		.v-input,
		.v-text-field,
		.v-select {
			width: 100%;
			max-width: 100%;
		}
	}
}
</style>

<style lang="scss">
/* Teleported to body — unscoped */
.v-tooltip__content.effort-report-bluetag-tooltip {
	opacity: 1 !important;
	background-color: #616161 !important;
	color: #fff !important;
	pointer-events: none;
}

.v-menu__content.effort-report-filter-menu {
	opacity: 1 !important;
	background: #fff !important;

	.v-list {
		background: #fff !important;
		opacity: 1 !important;
	}

	.v-list-item {
		opacity: 1 !important;

		&::before {
			opacity: 0 !important;
			background: transparent !important;
		}

		&:hover,
		&.v-list-item--highlighted,
		&.v-list-item--active {
			opacity: 1 !important;
			background-color: #e8eef5 !important;
			color: inherit !important;

			&::before {
				opacity: 0 !important;
			}

			.v-list-item__title,
			.v-list-item__subtitle,
			.v-list-item__action,
			.v-icon {
				opacity: 1 !important;
				color: inherit !important;
			}
		}
	}
}

.theme--dark .v-menu__content.effort-report-filter-menu {
	background: #1e1e1e !important;

	.v-list {
		background: #1e1e1e !important;
	}

	.v-list-item:hover,
	.v-list-item.v-list-item--highlighted,
	.v-list-item.v-list-item--active {
		background-color: #2f2f2f !important;
	}
}
</style>
